<template>
  <ModalComponent
    class="cnmc-send-review-modal"
    submitButtonText=""
    :hideSubmitButton="true"
    size="xl"
    @setForceToggle="setForceToggle"
    @close="close"
  >
    <template #title>
      {{ t("cnmc.outbound.review.title") }}
    </template>
    <template #body>
      <div v-if="preview" class="py-3 d-flex flex-column gap-3">
        <div v-if="notice" class="alert alert-subtle-info mb-0" role="status">
          {{ notice }}
        </div>

        <CnmcOutboundFacts
          :holder="preview.holder"
          :recipient="preview.recipient"
          :destination="preview.destination"
          :totalMwh="preview.total_mwh"
          :filingDeadline="preview.filing_deadline"
        >
          <div class="col-sm-6 col-lg-3">
            <dt class="small text-body-secondary fw-normal">
              {{ t("cnmc.outbound.review.route") }}
            </dt>
            <dd class="fw-medium mb-0">
              {{
                preview.route ? t(`cnmc.outbound.routes.${preview.route}`) : "-"
              }}
            </dd>
          </div>
        </CnmcOutboundFacts>

        <div
          v-if="preview.blocking.length"
          class="alert alert-subtle-danger mb-0"
          data-testid="cnmc-blocking"
        >
          <ul class="mb-0 ps-3">
            <li v-for="issue in preview.blocking" :key="issue.code">
              {{ issueText(issue) }}
            </li>
          </ul>
          <button
            v-if="canApplySuggestion"
            type="button"
            class="btn btn-sm btn-outline-primary mt-2"
            :disabled="isLoading"
            @click="emit('applySuggestion')"
          >
            {{ t("cnmc.outbound.review.use_oldest") }}
          </button>
        </div>

        <div
          v-if="preview.warnings.length"
          class="alert alert-subtle-warning mb-0"
          data-testid="cnmc-warnings"
        >
          <ul class="mb-0 ps-3">
            <li v-for="issue in preview.warnings" :key="issue.code">
              {{ issueText(issue) }}
            </li>
          </ul>
        </div>

        <div
          v-if="!preview.profile_ready"
          class="alert alert-subtle-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0"
          data-testid="cnmc-profile-missing"
        >
          <span>{{ t("cnmc.outbound.profile_missing") }}</span>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="isLoading"
            @click="emit('completeProfile')"
          >
            {{ t("cnmc.outbound.review.complete_profile") }}
          </button>
        </div>

        <div>
          <div class="eyebrow-label mb-2">
            {{
              t("cnmc.outbound.review.rows", {
                count: preview.row_count,
              })
            }}
          </div>
          <CnmcOutboundRowsTable :rows="preview.groups">
            <template #headers>
              <th scope="col">{{ t("cnmc.outbound.review.months") }}</th>
              <th scope="col" class="text-end">MWh</th>
            </template>
            <template #cells="{ row }">
              <td class="small">
                <div v-for="month in row.months" :key="month.month">
                  {{ formatCnmcMonthCount(month, locale) }} MWh
                </div>
              </td>
              <td class="text-end fw-semibold">
                {{ formatNumberWithSpaces(row.go_count) }}
              </td>
            </template>
          </CnmcOutboundRowsTable>
          <p class="small text-body-secondary mt-2 mb-0">
            {{ t("cnmc.outbound.review.what_cnmc_moves") }}
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <button type="button" class="btn-ghost" @click="close">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-success"
        :disabled="confirmDisabled"
        @click="emit('confirm')"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("cnmc.outbound.review.confirm") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import CnmcOutboundFacts from "@/dashboard/production_strategy/issuance-reporting/es/CnmcOutboundFacts.vue";
import CnmcOutboundRowsTable from "@/dashboard/production_strategy/issuance-reporting/es/CnmcOutboundRowsTable.vue";
import { formatCnmcMonthCount } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import type {
  OutboundIssue,
  OutboundPreviewResponse,
} from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Reviews what a CNMC transfer or export will file before it is prepared: holder, route, recipient,
 * the form rows with the months CNMC will debit, and anything that blocks it. Confirming emits only;
 * the parent closes the modal once the filing exists, and keeps it open to re-preview on a 409.
 */
const props = defineProps<{
  preview: OutboundPreviewResponse | null;
  notice: string | null;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  confirm: [];
  applySuggestion: [];
  completeProfile: [];
}>();

const visible = defineModel<boolean>({ required: true });

const { t, te, locale } = useI18n();

const forceToggle = ref<((value: boolean) => void) | null>(null);

const confirmDisabled = computed(
  () =>
    !props.preview ||
    props.isLoading ||
    props.preview.blocking.length > 0 ||
    !props.preview.profile_ready
);

const canApplySuggestion = computed(
  () =>
    !!props.preview?.suggested_items?.length &&
    props.preview.blocking.some((issue) => issue.code === "NOT_OLDEST_FIRST")
);

const issueText = (issue: OutboundIssue) => {
  const key = `cnmc.outbound.issues.${issue.code}`;
  return te(key)
    ? t(key, { deadline: issue.deadline ? formatDate(issue.deadline) : "" })
    : issue.code;
};

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

watch(visible, (open) => forceToggle.value?.(open));

const close = () => {
  visible.value = false;
};
</script>
